/**
 * DeliveryOptionsModal Component
 * Main modal for selecting delivery options per supplier before execution
 */

import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useIsMutating } from "@tanstack/react-query";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Box,
  Text,
  VStack,
  HStack,
  Button,
  Badge,
  Flex,
  IconButton,
  Icon,
  Tooltip,
  useDisclosure,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Tabs,
  TabList,
  Tab,
  useToast,
} from "@chakra-ui/react";
import { MdClose, MdInfo, MdWarning } from "react-icons/md";
import { SingleInvoiceToggle } from "../../views/Orders/components/SingleInvoiceToggle";
import {
  hasConfirmedNonDeletedChildOrder,
  hasGaraazInvoiceUploaded,
} from "../../views/Orders/orderUtils";
import {
  DeliveryOptionsModalProps,
  SelectedDeliveryOption,
  EnrichedSupplierForDelivery,
  NormalizedDeliverySelection,
  EstimateSplitDeliveryType,
  DeliveryOption,
} from "./types";
import { useCountdownTimer } from "./hooks/useCountdownTimer";
import EstimateSplitCard from "./EstimateSplitCard/index";
import { isCptAuditPopoverScrollTarget } from "./EstimateSplitCard/components/DeliveryOptionCptAuditInfo";
import OrderCancellationModal from "../../views/Orders/components/OrderCancellationModal";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { calculateDiscountedPrice } from "./utils/deliveryCostUtils";
import {
  getDefaultSelectedVehicleType,
  getEffectiveDeliveryCost,
  getVehicleTypeCostsFromSplit,
  getWheelClassForVehicleType,
} from "./utils/vehicleTypeUtils";
import {
  calculateRemainingItemsMRP,
  calculateSupplierFulfillingItemsMRP,
} from "./utils/deliveryItemUtils";
import { Roles } from "../../views/Orders/enums";
import { useUserDetailsContext } from "../../context/userDetailsContext";
import {
  hasSupplierSplitWithFacilitation,
  isSupplierSplitValidForSingleInvoice,
} from "./utils/estimateSingleInvoiceValidation";
import {
  buildTradingSelfPickupValidationContext,
  hasTradingSplitWithSelfPickupOptions,
  isSupplierSplitValidForTradingSelfPickup,
  TRADING_SELF_PICKUP_BLOCKED_MESSAGE,
} from "./utils/estimateTradingSelfPickupValidation";
import {
  getDigitalEstimateIneligibilityReason,
  isProceedBlockedByDigitalEstimate,
} from "./utils/requiresDigitalEstimateShare";
import { estimateSectionHeaderProps } from "./utils/estimateSectionHeaderStyles";
import {
  toEstimateCptMaxUtcIso,
  validateEstimateMaxBounds,
} from "./utils/estimateMaxBounds";
import { isEnquiryWhitelistedForLatestEstimateUi } from "./utils/isEnquiryWhitelistedForLatestEstimateUi";
import { extractApiErrorMessage } from "./utils/apiErrorUtils";
import { usePatchEstimate } from "../../views/Orders/hooks/usePatchEstimate";
import { usePatchEstimateSplit } from "../../views/Orders/hooks/usePatchEstimateSplit";
import { useGetOrderMediaConfig } from "../../views/PlaceOrderTest/queryHooks";
import { useGetAllSuppliers } from "../../views/Orders/queryHooks";

dayjs.extend(utc);
dayjs.extend(timezone);

const MAX_BOUNDS_DEBOUNCE_MS = 500;

/**
 * Check if a supplier's selection is complete
 * Valid states: Hold, Cancel, BuyTime, or Confirm with valid delivery details
 */
const isSelectionComplete = (
  supplier: EnrichedSupplierForDelivery,
  selection: SelectedDeliveryOption | undefined,
): boolean => {
  if (!selection) return false;

  // Hold, Cancel, and BuyTime are always valid actions
  if (selection.customerAction === "hold") return true;
  if (selection.customerAction === "cancel") return true;
  if (selection.customerAction === "buytime") {
    // BuyTime requires a hours value
    return !!selection.buyTimeHours;
  }

  // Confirm requires valid delivery details
  if (selection.customerAction === "confirm") {
    const details = selection.deliveryDetails;
    if (!details) return false;

    if (details.selectionType === "option") {
      const { selectedDeliveryOptionId } = details;
      if (!selectedDeliveryOptionId) return false;
      return supplier.deliveryOptions.some(
        (o) => o._id === selectedDeliveryOptionId,
      );
    }

    if (details.selectionType === "manual") {
      // Manual form is open but not yet saved. The user cannot proceed until it is saved
      // (which changes selectionType to "option") or they select a different option.
      return false;
    }
  }

  return false;
};

const isSupplierSplitTransactionAndPaymentConfigured = (
  supplier: EnrichedSupplierForDelivery,
): boolean => {
  if (supplier.supplierId == null || supplier.supplierId === undefined) {
    return true;
  }
  if (!supplier.transactionType) {
    return false;
  }
  if (supplier.payableBy == null || supplier.payableTo == null) {
    return false;
  }
  return true;
};

const getActiveDeliveryOptions = (
  supplier: EnrichedSupplierForDelivery,
): EnrichedSupplierForDelivery["deliveryOptions"] =>
  (supplier.deliveryOptions ?? []).filter(
    (option) => !(option as { isDeleted?: boolean }).isDeleted,
  );

const isSupplierSplitFullyConfigured = (
  supplier: EnrichedSupplierForDelivery,
): boolean => {
  if (!isSupplierSplitTransactionAndPaymentConfigured(supplier)) {
    return false;
  }
  if (supplier.supplierId == null || supplier.supplierId === undefined) {
    return true;
  }
  return getActiveDeliveryOptions(supplier).length > 0;
};

const getSupplierKey = (
  supplierId: string | undefined | null,
  index: number,
  splitId?: string,
): string => {
  // Prefer splitId so dual-bucket paths can share a supplierId without
  // colliding selection state (fastest + cheapest for the same supplier).
  if (splitId) return splitId;
  if (supplierId == null) return `remaining-items-${index}`;
  return `${supplierId}-${index}`;
};

/**
 * Normalize a single supplier selection into a lean, downstream-friendly shape.
 * Ensures CPT and delivery cost are present for confirm actions, whether the user
 * selected a predefined option or entered values manually.
 * For buytime actions, calculates the ISO datetime from hours + confirmationTime.
 */
const normalizeSelection = (
  supplier: EnrichedSupplierForDelivery,
  selection: SelectedDeliveryOption | undefined,
  confirmationTime: string,
): NormalizedDeliverySelection => {
  if (!selection) {
    // Should not happen when canConfirm is true, but provide a safe fallback.
    return {
      supplierId: supplier.supplierId,
      splitId: supplier.splitId,
      customerAction: "hold",
    };
  }

  const base: NormalizedDeliverySelection = {
    supplierId: selection.supplierId,
    splitId: supplier.splitId,
    customerAction: selection.customerAction,
  };

  // Non-confirm actions: pass through as-is (no CPT / cost needed).
  if (
    selection.customerAction === "hold" ||
    selection.customerAction === "cancel"
  ) {
    return base;
  }

  if (selection.customerAction === "buytime") {
    // Calculate buyTimeValue as confirmationTime + hours (using UTC consistently)
    let buyTimeValue: string | undefined;
    if (selection.buyTimeHours) {
      const hours = parseFloat(selection.buyTimeHours);
      if (!isNaN(hours) && hours > 0) {
        buyTimeValue = dayjs(confirmationTime)
          .add(hours, "hours")
          .toISOString();
      }
    }
    return {
      ...base,
      buyTimeValue,
      buyTimeHours: selection.buyTimeHours,
    };
  }

  // Confirm actions: include CPT and delivery cost in a unified format.
  const details = selection.deliveryDetails;
  if (!details) {
    return base;
  }

  if (details.selectionType === "option") {
    const option = supplier.deliveryOptions.find(
      (o) => o._id === details.selectedDeliveryOptionId,
    );

    if (!option) {
      return base;
    }

    const effectiveCost = getEffectiveDeliveryCost(
      option,
      supplier.selectedVehicleType,
    );
    const deliveryCost = calculateDiscountedPrice(
      effectiveCost,
      option.deliveryCostDiscountPercentage,
    );
    const vehicleTypeOptions = getVehicleTypeCostsFromSplit(supplier);
    const selectedVehicleType = supplier.selectedVehicleType;
    const vehicleType = getWheelClassForVehicleType(
      vehicleTypeOptions,
      selectedVehicleType,
      supplier.vehicleType,
    );

    return {
      ...base,
      cptSlab: option.cptSlab,
      cptMin: option.cptMin,
      cptMax: option.cptMax,
      deliveryCost,
      vehicleType,
      selectedVehicleType,
      selectionSource: "option",
      deliveryOptionId: option._id,
      deliveryMode: option.deliveryMode,
    };
  }

  if (details.selectionType === "manual") {
    // Should be caught by isSelectionComplete, but providing a failsafe fallback.
    return base;
  }

  return base;
};

const DeliveryOptionsModal: React.FC<DeliveryOptionsModalProps> = ({
  isOpen,
  onClose,
  onDismiss,
  orderNo,
  suppliers,
  expiresAt,
  estimateNo,
  onShareEstimate,
  isEstimateShared = false,
  hasSharedEstimateForEnquiry = false,
  maxSplitAllowedShareToCustomer = 1,
  onConfirm,
  onOptionUpdate,
  onOptionAdd,
  onOptionDelete,
  onHoldOrder,
  onClearBucket,
  quickEtasMins,
  onCancelOrder,
  orderCancellationReasons = [],
  instantDeliveryMinimumDeliveryCost,
  isSingleGaraazInvoiceRequired,
  orderId = "",
  orderData,
  refetchOrders,
  garaazSupplierIdFromConfig,
  childOrders,
  onSupplierSplitConfigUpdate,
  onSingleInvoiceToggleChange,
  onRemovePartFromSplit,
  canRemovePartsFromSplits = false,
}) => {
  // State for selections (keyed by supplierId or "remaining-items")
  const [selections, setSelections] = useState<
    Map<string, SelectedDeliveryOption>
  >(new Map());
  const [selectedVehicleTypeBySplitId, setSelectedVehicleTypeBySplitId] =
    useState<Map<string, string>>(new Map());
  const [maxCptLocal, setMaxCptLocal] = useState("");
  const [maxDeliveryCost, setMaxDeliveryCost] = useState<number | undefined>(
    undefined,
  );
  /** Per-tab max bounds for dual-bucket (fastest/cheapest) estimates. */
  const [boundsByDeliveryType, setBoundsByDeliveryType] = useState<
    Record<
      EstimateSplitDeliveryType,
      { maxCptLocal: string; maxDeliveryCost: number | undefined }
    >
  >({
    FASTEST: { maxCptLocal: "", maxDeliveryCost: undefined },
    CHEAPEST: { maxCptLocal: "", maxDeliveryCost: undefined },
  });
  const [isMaxBoundsPending, setIsMaxBoundsPending] = useState(false);
  const [maxBoundsSyncError, setMaxBoundsSyncError] = useState<string | null>(
    null,
  );
  const maxBoundsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const lastSyncedBoundsByTypeRef = useRef<
    Record<
      EstimateSplitDeliveryType,
      { cptMax: string | null; deliveryCostMax: number | null }
    >
  >({
    FASTEST: { cptMax: null, deliveryCostMax: null },
    CHEAPEST: { cptMax: null, deliveryCostMax: null },
  });
  const patchEstimateMutation = usePatchEstimate();
  const { mutateAsync: patchEstimateSplitAsync } = usePatchEstimateSplit();
  const toast = useToast();
  const vehicleTypeSyncInFlightRef = useRef<Set<string>>(new Set());
  const isMutatingCount = useIsMutating({ mutationKey: ["deliveryOption"] });
  const isSplitPatchMutating =
    useIsMutating({ mutationKey: ["estimateSplit"] }) > 0;
  const isEstimatePatchMutating =
    useIsMutating({ mutationKey: ["estimate"] }) > 0;
  const isMutating =
    isMutatingCount > 0 || isSplitPatchMutating || isEstimatePatchMutating;
  const userDetails = useUserDetailsContext();
  const { data: mobileConfigs } = useGetOrderMediaConfig();
  const { data: allSuppliers } = useGetAllSuppliers();

  const isLatestEstimateUiWhitelisted = useMemo(
    () =>
      isEnquiryWhitelistedForLatestEstimateUi(
        (
          mobileConfigs as {
            whitelisted_enquiry_nos_for_latest_estimate_ui?: unknown;
          }
        )?.whitelisted_enquiry_nos_for_latest_estimate_ui,
        orderNo,
        orderData,
      ),
    [mobileConfigs, orderNo, orderData],
  );

  const showDeliveryTypeTabs = useMemo(
    () =>
      isLatestEstimateUiWhitelisted &&
      suppliers.some(
        (s) => s.deliveryType === "FASTEST" || s.deliveryType === "CHEAPEST",
      ),
    [isLatestEstimateUiWhitelisted, suppliers],
  );

  // Max CPT / Max Delivery Cost only when Fastest/Cheapest tabs are visible.
  const isMaxBoundsSectionVisible = showDeliveryTypeTabs;

  const [activeDeliveryTypeTab, setActiveDeliveryTypeTab] =
    useState<EstimateSplitDeliveryType>("FASTEST");

  useEffect(() => {
    if (showDeliveryTypeTabs) {
      setActiveDeliveryTypeTab("FASTEST");
    }
  }, [showDeliveryTypeTabs, estimateNo]);

  const suppliersWithSelectedVehicleType = useMemo(
    () =>
      suppliers.map((supplier) => ({
        ...supplier,
        selectedVehicleType:
          selectedVehicleTypeBySplitId.get(supplier.splitId) ??
          getDefaultSelectedVehicleType(supplier),
      })),
    [suppliers, selectedVehicleTypeBySplitId],
  );

  const handleSelectedVehicleTypeChange = useCallback(
    async (splitId: string, vehicleType: string) => {
      if (!splitId || vehicleTypeSyncInFlightRef.current.has(splitId)) {
        return;
      }
      const supplier = suppliersWithSelectedVehicleType.find(
        (entry) => entry.splitId === splitId,
      );
      const previous = supplier?.selectedVehicleType;
      if (!vehicleType || vehicleType === previous) {
        return;
      }

      vehicleTypeSyncInFlightRef.current.add(splitId);
      setSelectedVehicleTypeBySplitId((prev) => {
        const next = new Map(prev);
        next.set(splitId, vehicleType);
        return next;
      });

      try {
        const estimate = await patchEstimateSplitAsync({
          estimateNo,
          splitId,
          payload: { selectedVehicleType: vehicleType },
        });
        const updatedSplit = (
          estimate as {
            splits?: Array<{
              _id?: unknown;
              vehicleType?: number;
              deliveryOptions?: DeliveryOption[];
            }>;
          }
        )?.splits?.find((split) => String(split._id) === String(splitId));
        const updatedDeliveryOptions = updatedSplit?.deliveryOptions;
        onSupplierSplitConfigUpdate(splitId, {
          selectedVehicleType: vehicleType,
          ...(typeof updatedSplit?.vehicleType === "number"
            ? { vehicleType: updatedSplit.vehicleType }
            : {}),
          ...(Array.isArray(updatedDeliveryOptions)
            ? { deliveryOptions: updatedDeliveryOptions }
            : {}),
        });
      } catch (error) {
        setSelectedVehicleTypeBySplitId((prev) => {
          const next = new Map(prev);
          if (previous) {
            next.set(splitId, previous);
          } else {
            next.delete(splitId);
          }
          return next;
        });
        toast({
          title: "Failed to update vehicle type",
          description: extractApiErrorMessage(
            error,
            "Could not save the selected vehicle type.",
          ),
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "top",
        });
      } finally {
        vehicleTypeSyncInFlightRef.current.delete(splitId);
      }
    },
    [
      estimateNo,
      onSupplierSplitConfigUpdate,
      patchEstimateSplitAsync,
      suppliersWithSelectedVehicleType,
      toast,
    ],
  );

  const visibleSuppliers = useMemo(() => {
    if (!showDeliveryTypeTabs) return suppliersWithSelectedVehicleType;
    return suppliersWithSelectedVehicleType.filter(
      (supplier) =>
        !supplier.deliveryType ||
        supplier.deliveryType === activeDeliveryTypeTab,
    );
  }, [
    showDeliveryTypeTabs,
    suppliersWithSelectedVehicleType,
    activeDeliveryTypeTab,
  ]);

  const tradingSelfPickupBaseContext = useMemo(
    () => ({
      orderData,
      mgpDistributorConfig: mobileConfigs?.mgp_distributor,
      allSuppliers,
    }),
    [orderData, mobileConfigs?.mgp_distributor, allSuppliers],
  );
  const SHARE_ESTIMATE_ALLOWED_USER_SUB_TYPES = [
    Roles.HEAD_OF_OPERATIONS,
    Roles.ORDER_HANDLER,
    Roles.EXECUTIVE,
    Roles.CODE_ALCHEMISTS,
  ] as const;
  const isUserAllowedToShareEstimate =
    SHARE_ESTIMATE_ALLOWED_USER_SUB_TYPES.includes(
      userDetails?.userSubType as (typeof SHARE_ESTIMATE_ALLOWED_USER_SUB_TYPES)[number],
    );
  // Cancellation modal state
  const {
    isOpen: isCancellationModalOpen,
    onOpen: onCancellationModalOpen,
    onClose: onCancellationModalClose,
  } = useDisclosure();

  // Countdown timer
  const { formattedTime, isExpired } = useCountdownTimer(expiresAt);

  // User-initiated close (X, Esc, outside click, timer expiry): let the
  // launcher discard the estimate if it is still untouched, then close.
  const handleDismiss = useCallback(() => {
    onDismiss?.();
    onClose();
  }, [onDismiss, onClose]);

  // Auto-close and clear bucket on timer expiry
  useEffect(() => {
    if (isExpired && isOpen) {
      onClearBucket?.();
      handleDismiss();
    }
  }, [isExpired, isOpen, onClearBucket, handleDismiss]);

  const isSingleInvoiceEnabled = Boolean(
    isSingleGaraazInvoiceRequired ??
    orderData?.billingDetails?.isSingleGaraazInvoiceRequired ??
    false,
  );

  // Check if all suppliers have valid selections
  const allSelectionsComplete = useMemo(() => {
    return suppliers.every((supplier, index) => {
      const key = getSupplierKey(supplier.supplierId, index, supplier.splitId);
      return isSelectionComplete(supplier, selections.get(key));
    });
  }, [suppliers, selections]);

  const allSplitsConfigured = useMemo(
    () =>
      suppliers.every((supplier) => {
        if (!isSupplierSplitFullyConfigured(supplier)) {
          return false;
        }
        return (
          isSupplierSplitValidForSingleInvoice(
            supplier,
            isSingleInvoiceEnabled,
          ) &&
          isSupplierSplitValidForTradingSelfPickup(
            supplier,
            buildTradingSelfPickupValidationContext({
              supplierId: supplier.supplierId,
              ...tradingSelfPickupBaseContext,
            }),
          )
        );
      }),
    [suppliers, isSingleInvoiceEnabled, tradingSelfPickupBaseContext],
  );

  const allSplitsHaveTransactionAndPaymentRoute = useMemo(
    () =>
      suppliers.every((supplier) =>
        isSupplierSplitTransactionAndPaymentConfigured(supplier),
      ),
    [suppliers],
  );

  const hasTradingWithSelfPickupOptions = useMemo(
    () =>
      hasTradingSplitWithSelfPickupOptions(
        suppliers,
        tradingSelfPickupBaseContext,
      ),
    [suppliers, tradingSelfPickupBaseContext],
  );

  const hasSupplierSplitWithNoOptions = useMemo(
    () =>
      suppliers.some(
        (s) =>
          s.supplierId != null && getActiveDeliveryOptions(s).length === 0,
      ),
    [suppliers],
  );

  const hasFacilitationWithSingleInvoice = useMemo(
    () => isSingleInvoiceEnabled && hasSupplierSplitWithFacilitation(suppliers),
    [isSingleInvoiceEnabled, suppliers],
  );

  const enforcedCustomerAreas = useMemo(
    () =>
      (
        mobileConfigs as
          | { enforce_digital_estimate_customer_areas?: string[] }
          | undefined
      )?.enforce_digital_estimate_customer_areas ?? [],
    [mobileConfigs],
  );

  const digitalEstimateEligibilityInput = useMemo(
    () => ({
      carMaker: orderData?.carMaker,
      customerArea: orderData?.customer?.area,
      enforcedCustomerAreas,
      hasSharedEstimateForEnquiry:
        hasSharedEstimateForEnquiry || isEstimateShared,
    }),
    [
      orderData?.carMaker,
      orderData?.customer?.area,
      enforcedCustomerAreas,
      hasSharedEstimateForEnquiry,
      isEstimateShared,
    ],
  );

  const digitalEstimateIneligibilityReason = useMemo(
    () => getDigitalEstimateIneligibilityReason(digitalEstimateEligibilityInput),
    [digitalEstimateEligibilityInput],
  );

  const isActionBlockedByDigitalEstimate = useMemo(
    () => isProceedBlockedByDigitalEstimate(digitalEstimateEligibilityInput),
    [digitalEstimateEligibilityInput],
  );

  const canConfirm =
    !isExpired &&
    allSelectionsComplete &&
    allSplitsConfigured &&
    !isMutating &&
    !isActionBlockedByDigitalEstimate;

  const digitalEstimateProceedTooltip =
    "Digital Estimate must be shared with the customer to proceed.";

  const proceedBlockedTooltip = isActionBlockedByDigitalEstimate
    ? digitalEstimateProceedTooltip
    : hasFacilitationWithSingleInvoice && !isExpired
      ? "Facilitation is not allowed with Single Invoice. Switch affected splits to Trading."
      : hasTradingWithSelfPickupOptions && !isExpired
        ? TRADING_SELF_PICKUP_BLOCKED_MESSAGE
        : !allSplitsHaveTransactionAndPaymentRoute && !isExpired
          ? "Set transaction type and payment route on every supplier split before proceeding"
          : hasSupplierSplitWithNoOptions && !isExpired
            ? "Each supplier split must have at least one delivery option before proceeding"
            : !allSelectionsComplete && !isExpired
              ? "Select a delivery option for every supplier split before proceeding"
              : "";

  const remainingItemsMRP = useMemo(() => {
    return calculateRemainingItemsMRP(suppliers);
  }, [suppliers]);

  const fulfilledItemsMRP = useMemo(() => {
    if (!suppliers || suppliers.length === 0) {
      return 0;
    }

    return suppliers.reduce((total, supplier) => {
      if (supplier.supplierId === null || supplier.supplierId === undefined) {
        return total;
      }
      return total + calculateSupplierFulfillingItemsMRP(supplier);
    }, 0);
  }, [suppliers]);

  // Handle selection change for a supplier
  const handleSelectionChange = useCallback(
    (selection: SelectedDeliveryOption | undefined, index: number) => {
      setSelections((prev) => {
        const next = new Map(prev);
        const supplier = suppliers[index];
        const key = getSupplierKey(
          supplier?.supplierId,
          index,
          supplier?.splitId,
        );

        if (!selection) {
          next.delete(key);
          return next;
        }

        next.set(key, selection);
        return next;
      });
    },
    [suppliers],
  );

  // Handle confirm button click
  const handleConfirm = useCallback(async () => {
    if (isActionBlockedByDigitalEstimate) return;
    // Capture confirmation time - used for calculating buy time ISO datetime
    const confirmationTime = new Date().toISOString();

    const normalizedSelections: NormalizedDeliverySelection[] =
      suppliersWithSelectedVehicleType.map((supplier, index) => {
        const key = getSupplierKey(
          supplier.supplierId,
          index,
          supplier.splitId,
        );
        const selection = selections.get(key);
        return normalizeSelection(supplier, selection, confirmationTime);
      });
    await onConfirm(
      normalizedSelections,
      suppliersWithSelectedVehicleType,
      estimateNo,
    );
  }, [
    isActionBlockedByDigitalEstimate,
    selections,
    suppliersWithSelectedVehicleType,
    onConfirm,
    estimateNo,
  ]);

  // Handle hold order
  const handleHoldOrder = useCallback(async () => {
    if (isActionBlockedByDigitalEstimate) return;
    if (onHoldOrder) {
      await onHoldOrder();
    }
  }, [isActionBlockedByDigitalEstimate, onHoldOrder]);

  // Handle cancel order
  const handleCancelOrderSubmit = useCallback(
    async (reason: string) => {
      if (isActionBlockedByDigitalEstimate) return;
      if (onCancelOrder) {
        await onCancelOrder(reason);
        onClose();
      }
    },
    [isActionBlockedByDigitalEstimate, onCancelOrder, onClose],
  );

  // Workaround for Chakra UI Modal wheel scroll propagation issues.
  // Manually handles wheel events to ensure consistent scrolling behavior
  // within the modal body and prevent scroll events from bubbling to parent elements.
  const handleBodyWheel = useCallback(
    (event: React.WheelEvent<HTMLDivElement>) => {
      if (isCptAuditPopoverScrollTarget(event.target)) {
        return;
      }

      const target = event.currentTarget;
      if (!target) return;
      target.scrollTop += event.deltaY;
      event.preventDefault();
    },
    [],
  );

  // Reset selections and estimate-level max bounds when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelections(new Map());
      setSelectedVehicleTypeBySplitId(new Map());
      setMaxCptLocal("");
      setMaxDeliveryCost(undefined);
      setBoundsByDeliveryType({
        FASTEST: { maxCptLocal: "", maxDeliveryCost: undefined },
        CHEAPEST: { maxCptLocal: "", maxDeliveryCost: undefined },
      });
      setIsMaxBoundsPending(false);
      setMaxBoundsSyncError(null);
      lastSyncedBoundsByTypeRef.current = {
        FASTEST: { cptMax: null, deliveryCostMax: null },
        CHEAPEST: { cptMax: null, deliveryCostMax: null },
      };
      if (maxBoundsDebounceRef.current) {
        clearTimeout(maxBoundsDebounceRef.current);
        maxBoundsDebounceRef.current = null;
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedVehicleTypeBySplitId((prev) => {
      const next = new Map(prev);
      let changed = false;
      const ids = new Set(suppliers.map((supplier) => supplier.splitId));
      for (const supplier of suppliers) {
        if (!next.has(supplier.splitId)) {
          const code = getDefaultSelectedVehicleType(supplier);
          if (code) {
            next.set(supplier.splitId, code);
            changed = true;
          }
        }
      }
      for (const key of Array.from(next.keys())) {
        if (!ids.has(key)) {
          next.delete(key);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [isOpen, suppliers]);

  useEffect(() => {
    return () => {
      if (maxBoundsDebounceRef.current) {
        clearTimeout(maxBoundsDebounceRef.current);
      }
    };
  }, []);

  const [isSharingEstimate, setIsSharingEstimate] = useState(false);

  const suppliersForActiveBounds = useMemo(() => {
    if (!showDeliveryTypeTabs) return suppliersWithSelectedVehicleType;
    return suppliersWithSelectedVehicleType.filter(
      (supplier) => supplier.deliveryType === activeDeliveryTypeTab,
    );
  }, [
    showDeliveryTypeTabs,
    suppliersWithSelectedVehicleType,
    activeDeliveryTypeTab,
  ]);

  // Rechecks whenever delivery options change (including after manual add/delete).
  const maxBoundsValidation = useMemo(
    () =>
      validateEstimateMaxBounds(
        suppliersForActiveBounds,
        maxCptLocal,
        maxDeliveryCost,
        activeDeliveryTypeTab,
      ),
    [
      suppliersForActiveBounds,
      maxCptLocal,
      maxDeliveryCost,
      activeDeliveryTypeTab,
    ],
  );

  const allDeliveryTypeBoundsValid = useMemo(() => {
    if (!showDeliveryTypeTabs) return maxBoundsValidation.isValid;
    const types: EstimateSplitDeliveryType[] = ["FASTEST", "CHEAPEST"];
    return types.every((type) => {
      const typeSuppliers = suppliersWithSelectedVehicleType.filter(
        (s) => s.deliveryType === type,
      );
      if (typeSuppliers.length === 0) return true;
      const bounds = boundsByDeliveryType[type];
      return validateEstimateMaxBounds(
        typeSuppliers,
        bounds.maxCptLocal,
        bounds.maxDeliveryCost,
        type,
      ).isValid;
    });
  }, [
    showDeliveryTypeTabs,
    maxBoundsValidation.isValid,
    suppliersWithSelectedVehicleType,
    boundsByDeliveryType,
  ]);

  const persistMaxBounds = useCallback(
    (cptLocal: string, cost: number | undefined) => {
      if (
        !estimateNo ||
        isExpired ||
        isEstimateShared ||
        !showDeliveryTypeTabs
      ) {
        setIsMaxBoundsPending(false);
        return;
      }

      const validation = validateEstimateMaxBounds(
        suppliersForActiveBounds,
        cptLocal,
        cost,
        activeDeliveryTypeTab,
      );
      const lastSynced =
        lastSyncedBoundsByTypeRef.current[activeDeliveryTypeTab];

      // Sync each field independently so one invalid field doesn't block the other.
      const payload: {
        deliveryType: EstimateSplitDeliveryType;
        cptMax?: string | null;
        deliveryCostMax?: number | null;
      } = {
        deliveryType: activeDeliveryTypeTab,
      };

      const nextCptMax = cptLocal ? toEstimateCptMaxUtcIso(cptLocal) : null;
      const nextDeliveryCostMax = cost === undefined ? null : Number(cost);

      if (validation.isCptValid && nextCptMax !== lastSynced.cptMax) {
        payload.cptMax = nextCptMax;
      }
      if (
        validation.isCostValid &&
        nextDeliveryCostMax !== lastSynced.deliveryCostMax
      ) {
        payload.deliveryCostMax = nextDeliveryCostMax;
      }

      if (
        payload.cptMax === undefined &&
        payload.deliveryCostMax === undefined
      ) {
        setIsMaxBoundsPending(false);
        return;
      }

      patchEstimateMutation.mutate(
        { estimateNo, payload },
        {
          onSuccess: () => {
            if (payload.cptMax !== undefined) {
              lastSynced.cptMax = payload.cptMax;
            }
            if (payload.deliveryCostMax !== undefined) {
              lastSynced.deliveryCostMax = payload.deliveryCostMax;
            }
            setIsMaxBoundsPending(false);
            setMaxBoundsSyncError(null);
          },
          onError: (error: unknown) => {
            setIsMaxBoundsPending(false);
            setMaxBoundsSyncError(
              extractApiErrorMessage(
                error,
                "Failed to update Max CPT / Max Delivery Cost.",
              ),
            );
          },
        },
      );
    },
    [
      estimateNo,
      isExpired,
      isEstimateShared,
      showDeliveryTypeTabs,
      suppliersForActiveBounds,
      activeDeliveryTypeTab,
      patchEstimateMutation,
    ],
  );

  const scheduleMaxBoundsSync = useCallback(
    (cptLocal: string, cost: number | undefined) => {
      if (!isMaxBoundsSectionVisible) return;
      // Show loading immediately; debounce the actual PATCH.
      setIsMaxBoundsPending(true);
      setMaxBoundsSyncError(null);
      if (maxBoundsDebounceRef.current) {
        clearTimeout(maxBoundsDebounceRef.current);
      }
      maxBoundsDebounceRef.current = setTimeout(() => {
        persistMaxBounds(cptLocal, cost);
      }, MAX_BOUNDS_DEBOUNCE_MS);
    },
    [isMaxBoundsSectionVisible, persistMaxBounds],
  );

  const isShareDisabled =
    Boolean(digitalEstimateIneligibilityReason) ||
    !isUserAllowedToShareEstimate ||
    isEstimateShared ||
    suppliers.length > maxSplitAllowedShareToCustomer ||
    !allSplitsHaveTransactionAndPaymentRoute ||
    hasSupplierSplitWithNoOptions ||
    !allSplitsConfigured ||
    hasTradingWithSelfPickupOptions ||
    (isMaxBoundsSectionVisible &&
      (!allDeliveryTypeBoundsValid || isMaxBoundsPending));

  // Associated + enquiry already SHARED: always that reason, never TT/PR/options/etc.
  const shareTooltipLabel = digitalEstimateIneligibilityReason
    ? digitalEstimateIneligibilityReason
    : !isUserAllowedToShareEstimate
      ? "Only HOO, Order Handlers, Executives, and Tech Team can share estimate"
      : isEstimateShared
        ? "An estimate was already shared for this enquiry"
        : hasFacilitationWithSingleInvoice
          ? "Facilitation is not allowed with Single Invoice. Switch affected splits to Trading."
          : hasTradingWithSelfPickupOptions
            ? TRADING_SELF_PICKUP_BLOCKED_MESSAGE
            : !allSplitsHaveTransactionAndPaymentRoute
              ? "Set transaction type and payment route on every supplier split before sharing"
              : hasSupplierSplitWithNoOptions
                ? "Each supplier split must have at least one delivery option before sharing"
                : suppliers.length > maxSplitAllowedShareToCustomer
                  ? `Can only share estimates with up to ${maxSplitAllowedShareToCustomer} supplier split(s)`
                  : isMaxBoundsSectionVisible && isMaxBoundsPending
                    ? "Saving Max CPT / Max Delivery Cost…"
                    : isMaxBoundsSectionVisible && !allDeliveryTypeBoundsValid
                      ? maxBoundsValidation.cptError ||
                        maxBoundsValidation.costError ||
                        "Fix Max CPT / Max Delivery Cost before sharing"
                      : "";

  const singleInvoiceLocked =
    !orderId ||
    !refetchOrders ||
    hasGaraazInvoiceUploaded(orderData ?? {}) ||
    hasConfirmedNonDeletedChildOrder(orderData, childOrders);

  const singleInvoiceLockTooltipLabel = useMemo(() => {
    if (!singleInvoiceLocked) return "";
    if (hasGaraazInvoiceUploaded(orderData ?? {})) {
      return "Single Invoice cannot be changed after a single invoice has been generated.";
    }
    if (hasConfirmedNonDeletedChildOrder(orderData, childOrders)) {
      return "Single Invoice cannot be changed because a confirmed order has been created.";
    }
    return "Single Invoice cannot be changed because a confirmed order has been created.";
  }, [singleInvoiceLocked, orderData, childOrders]);

  const singleInvoiceToggleTooltip = useMemo(() => {
    if (singleInvoiceLocked && singleInvoiceLockTooltipLabel) {
      return singleInvoiceLockTooltipLabel;
    }
    if (
      !isSingleInvoiceEnabled &&
      hasSupplierSplitWithFacilitation(suppliers)
    ) {
      return "Facilitation is not available when Single Invoice is required. All splits transaction type will be changed to Trading.";
    }
    return "";
  }, [
    singleInvoiceLocked,
    singleInvoiceLockTooltipLabel,
    isSingleInvoiceEnabled,
    suppliers,
  ]);

  const handleMaxCptChange = useCallback(
    (value: string) => {
      setMaxCptLocal(value);
      if (showDeliveryTypeTabs) {
        setBoundsByDeliveryType((prev) => ({
          ...prev,
          [activeDeliveryTypeTab]: {
            ...prev[activeDeliveryTypeTab],
            maxCptLocal: value,
          },
        }));
      }
      scheduleMaxBoundsSync(value, maxDeliveryCost);
    },
    [
      scheduleMaxBoundsSync,
      maxDeliveryCost,
      showDeliveryTypeTabs,
      activeDeliveryTypeTab,
    ],
  );

  const handleMaxDeliveryCostChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value.replace(/[^\d]/g, "");
      if (raw === "") {
        setMaxDeliveryCost(undefined);
        if (showDeliveryTypeTabs) {
          setBoundsByDeliveryType((prev) => ({
            ...prev,
            [activeDeliveryTypeTab]: {
              ...prev[activeDeliveryTypeTab],
              maxDeliveryCost: undefined,
            },
          }));
        }
        scheduleMaxBoundsSync(maxCptLocal, undefined);
        return;
      }
      const value = parseInt(raw, 10);
      const next = Number.isNaN(value) ? 0 : Math.max(0, value);
      setMaxDeliveryCost(next);
      if (showDeliveryTypeTabs) {
        setBoundsByDeliveryType((prev) => ({
          ...prev,
          [activeDeliveryTypeTab]: {
            ...prev[activeDeliveryTypeTab],
            maxDeliveryCost: next,
          },
        }));
      }
      scheduleMaxBoundsSync(maxCptLocal, next);
    },
    [
      scheduleMaxBoundsSync,
      maxCptLocal,
      showDeliveryTypeTabs,
      activeDeliveryTypeTab,
    ],
  );

  const handleDeliveryTypeTabChange = useCallback(
    (index: number) => {
      const nextTab: EstimateSplitDeliveryType =
        index === 0 ? "FASTEST" : "CHEAPEST";
      if (showDeliveryTypeTabs) {
        setBoundsByDeliveryType((prev) => ({
          ...prev,
          [activeDeliveryTypeTab]: { maxCptLocal, maxDeliveryCost },
        }));
        const nextBounds = boundsByDeliveryType[nextTab];
        setMaxCptLocal(nextBounds.maxCptLocal);
        setMaxDeliveryCost(nextBounds.maxDeliveryCost);
      }
      setActiveDeliveryTypeTab(nextTab);
    },
    [
      showDeliveryTypeTabs,
      activeDeliveryTypeTab,
      maxCptLocal,
      maxDeliveryCost,
      boundsByDeliveryType,
    ],
  );

  const handleShareEstimateClick = useCallback(async () => {
    if (!onShareEstimate) return;
    if (
      isMaxBoundsSectionVisible &&
      (!allDeliveryTypeBoundsValid || isMaxBoundsPending)
    ) {
      return;
    }
    try {
      setIsSharingEstimate(true);
      await onShareEstimate();
    } finally {
      setIsSharingEstimate(false);
    }
  }, [
    onShareEstimate,
    isMaxBoundsSectionVisible,
    allDeliveryTypeBoundsValid,
    isMaxBoundsPending,
  ]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleDismiss}
      size="5xl"
      scrollBehavior="inside"
      isCentered
      closeOnOverlayClick={false}
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        maxH="90vh"
        borderRadius="xl"
        overflow="hidden"
        fontFamily="'Nunito','Inter', sans-serif"
      >
        {/* Header */}
        <ModalHeader
          bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
          borderBottom="none"
          py={4}
          px={6}
          position="relative"
        >
          <Flex justify="space-between" align="center" pr={10}>
            <VStack align="flex-start" spacing={0}>
              <Text
                fontSize="1.15rem"
                fontWeight="bold"
                color="white"
                fontFamily="'Nunito','Inter', sans-serif"
              >
                Estimate
              </Text>
              <Text
                fontSize="0.78rem"
                color="whiteAlpha.800"
                fontFamily="'Nunito','Inter', sans-serif"
                lineHeight="1.1"
              >
                #{orderNo}
              </Text>
            </VStack>

            {/* Timer Badge */}
            <HStack
              bg={isExpired ? "orange.400" : "whiteAlpha.200"}
              px={3}
              py={1.5}
              borderRadius="md"
              spacing={2}
              backdropFilter="blur(4px)"
            >
              <Box
                w={2}
                h={2}
                borderRadius="full"
                bg={isExpired ? "white" : "whiteAlpha.800"}
              />
              <Text
                fontSize="0.8rem"
                color="white"
                fontFamily="'Nunito','Inter', sans-serif"
              >
                Estimate expires in
              </Text>
              <Badge
                bg={isExpired ? "white" : "orange.400"}
                color={isExpired ? "orange.400" : "white"}
                px={2}
                py={0.5}
                borderRadius="md"
                fontSize="0.85rem"
                fontWeight="bold"
              >
                {formattedTime}
              </Badge>
            </HStack>
          </Flex>

          {/* Close Button */}
          <IconButton
            aria-label="Close modal"
            icon={<MdClose size={20} />}
            position="absolute"
            top={4}
            right={4}
            size="sm"
            variant="ghost"
            color="white"
            _hover={{ bg: "whiteAlpha.200" }}
            onClick={handleDismiss}
          />
        </ModalHeader>

        {/* Body: fixed header (through Single Invoice), scrollable supplier cards */}
        <ModalBody
          bg="#f8f9fc"
          px={6}
          py={4}
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <Box
            flexShrink={0}
            pb={2}
            bg="#f8f9fc"
            zIndex={1}
            borderBottom="1px solid"
            borderColor="gray.200"
          >
            {showDeliveryTypeTabs && (
              <Tabs
                index={activeDeliveryTypeTab === "FASTEST" ? 0 : 1}
                onChange={handleDeliveryTypeTabChange}
                variant="unstyled"
                mb={3}
                size="sm"
                w="100%"
              >
                <TabList
                  w="100%"
                  bg="gray.100"
                  borderRadius="lg"
                  p="3px"
                  border="1px solid"
                  borderColor="gray.200"
                >
                  <Tab
                    flex="1"
                    justifyContent="center"
                    fontFamily="'Nunito','Inter', sans-serif"
                    fontWeight="700"
                    fontSize="0.85rem"
                    borderRadius="md"
                    color="gray.500"
                    py={2}
                    _selected={{
                      bg: "#4b85f2",
                      color: "white",
                      boxShadow: "sm",
                    }}
                    _hover={{
                      color: "gray.700",
                      _selected: { color: "white" },
                    }}
                  >
                    Fastest
                  </Tab>
                  <Tab
                    flex="1"
                    justifyContent="center"
                    fontFamily="'Nunito','Inter', sans-serif"
                    fontWeight="700"
                    fontSize="0.85rem"
                    borderRadius="md"
                    color="gray.500"
                    py={2}
                    _selected={{
                      bg: "#4b85f2",
                      color: "white",
                      boxShadow: "sm",
                    }}
                    _hover={{
                      color: "gray.700",
                      _selected: { color: "white" },
                    }}
                  >
                    Cheapest
                  </Tab>
                </TabList>
              </Tabs>
            )}
            <Flex justify="space-between" align="flex-start" gap={4}>
              <Box>
                <Text {...estimateSectionHeaderProps}>Suppliers</Text>
                <Text
                  fontSize="0.8rem"
                  color="gray.500"
                  fontFamily="'Nunito','Inter', sans-serif"
                >
                  Review part details and configurations for all split(s).
                </Text>
              </Box>

              <HStack spacing={4} align="flex-start">
                {isMaxBoundsSectionVisible && (
                  <HStack spacing={3} align="start">
                    <Box w="200px">
                      <Flex align="center" gap={1} mb={1}>
                        <Text
                          fontSize="0.7rem"
                          color="gray.500"
                          fontWeight="700"
                          fontFamily="'Nunito','Inter', sans-serif"
                          letterSpacing="wider"
                        >
                          MAX CPT TIME
                        </Text>
                        {isMaxBoundsPending && (
                          <Spinner size="xs" color="blue.400" thickness="2px" />
                        )}
                      </Flex>
                      <Input
                        type="datetime-local"
                        size="sm"
                        fontSize="0.85rem"
                        fontWeight="500"
                        placeholder="Select Max CPT"
                        borderColor={
                          maxBoundsValidation.cptError ? "red.300" : "gray.300"
                        }
                        _focus={{
                          borderColor: "blue.400",
                          boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)",
                        }}
                        value={maxCptLocal}
                        onChange={(e) => handleMaxCptChange(e.target.value)}
                        isDisabled={
                          isExpired ||
                          isEstimateShared ||
                          (isEstimatePatchMutating && !isMaxBoundsPending)
                        }
                      />
                      {maxBoundsValidation.cptError && (
                        <Text
                          fontSize="12px"
                          fontWeight="semibold"
                          color="red.500"
                          mt={1}
                          ml={1}
                        >
                          *{maxBoundsValidation.cptError}
                        </Text>
                      )}
                    </Box>
                    <Box w="150px">
                      <Flex align="center" gap={1} mb={1}>
                        <Text
                          fontSize="0.7rem"
                          color="gray.500"
                          fontWeight="700"
                          fontFamily="'Nunito','Inter', sans-serif"
                          letterSpacing="wider"
                        >
                          MAX DELIVERY COST
                        </Text>
                        {isMaxBoundsPending && (
                          <Spinner size="xs" color="blue.400" thickness="2px" />
                        )}
                      </Flex>
                      <InputGroup size="sm">
                        <InputLeftElement
                          pointerEvents="none"
                          fontSize="0.8rem"
                          color="gray.500"
                        >
                          ₹
                        </InputLeftElement>
                        <Input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          placeholder="0"
                          fontSize="0.85rem"
                          fontWeight="600"
                          textAlign="right"
                          value={maxDeliveryCost ?? ""}
                          onChange={handleMaxDeliveryCostChange}
                          isDisabled={
                            isExpired ||
                            isEstimateShared ||
                            (isEstimatePatchMutating && !isMaxBoundsPending)
                          }
                          borderColor={
                            maxBoundsValidation.costError
                              ? "red.300"
                              : "gray.300"
                          }
                          _focus={{
                            borderColor: "blue.400",
                            boxShadow:
                              "0 0 0 1px var(--chakra-colors-blue-400)",
                          }}
                        />
                      </InputGroup>
                      {maxBoundsValidation.costError && (
                        <Text
                          fontSize="12px"
                          fontWeight="semibold"
                          color="red.500"
                          mt={1}
                          ml={1}
                        >
                          *{maxBoundsValidation.costError}
                        </Text>
                      )}
                    </Box>
                  </HStack>
                )}

                <VStack align="flex-end" spacing={0}>
                  <Tooltip
                    label="Total MRP of Available Parts / Total MRP of Unavailable Parts"
                    hasArrow
                    placement="top"
                    shouldWrapChildren
                  >
                    <HStack align="center" spacing={2} cursor="help">
                      <Text {...estimateSectionHeaderProps} whiteSpace="nowrap">
                        MRP:
                      </Text>
                      <Text {...estimateSectionHeaderProps} whiteSpace="nowrap">
                        ₹{fulfilledItemsMRP.toLocaleString("en-IN")}
                      </Text>
                      <Text {...estimateSectionHeaderProps}>/</Text>
                      <Text
                        {...estimateSectionHeaderProps}
                        color="red.700"
                        whiteSpace="nowrap"
                      >
                        ₹{remainingItemsMRP.toLocaleString("en-IN")}
                      </Text>
                    </HStack>
                  </Tooltip>

                  <HStack spacing={1} align="center">
                    <Tooltip
                      label={singleInvoiceToggleTooltip}
                      isDisabled={!singleInvoiceToggleTooltip}
                      hasArrow
                      placement="top"
                      shouldWrapChildren
                    >
                      <Box display="inline-flex">
                        <SingleInvoiceToggle
                          orderId={orderId}
                          isChecked={isSingleInvoiceEnabled}
                          isLocked={singleInvoiceLocked}
                          refetchAllOrders={refetchOrders ?? (() => {})}
                          onAfterToggleSuccess={onSingleInvoiceToggleChange}
                        />
                      </Box>
                    </Tooltip>
                    <Tooltip
                      label="Enabling this will allow you to create a single invoice for all confirmed orders. No individual invoice will be created automatically."
                      hasArrow
                      placement="top"
                    >
                      <Box as="span" display="inline-flex" cursor="help">
                        <Icon as={MdInfo} boxSize={4} color="gray.500" />
                      </Box>
                    </Tooltip>
                  </HStack>
                </VStack>
              </HStack>
            </Flex>
            {isMaxBoundsSectionVisible && maxBoundsSyncError && (
              <Text
                fontSize="12px"
                fontWeight="semibold"
                color="red.500"
                mt={1}
                textAlign="right"
              >
                *{maxBoundsSyncError}
              </Text>
            )}
          </Box>

          <Box
            flex="1"
            minH={0}
            overflowY="auto"
            pt={4}
            onWheelCapture={handleBodyWheel}
            css={{
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-track": {
                background: "#f1f1f1",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#c1c1c1",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "#a8a8a8",
              },
            }}
          >
            <VStack align="stretch" spacing={4}>
              {visibleSuppliers.map((supplier) => {
                const index = suppliers.findIndex(
                  (s) => s.splitId === supplier.splitId,
                );
                const resolvedIndex = index >= 0 ? index : 0;
                return (
                  <EstimateSplitCard
                    key={getSupplierKey(
                      supplier.supplierId,
                      resolvedIndex,
                      supplier.splitId,
                    )}
                    estimateNo={estimateNo}
                    orderNo={orderNo}
                    supplier={supplier}
                    selection={selections.get(
                      getSupplierKey(
                        supplier.supplierId,
                        resolvedIndex,
                        supplier.splitId,
                      ),
                    )}
                    onSelectionChange={(selection) =>
                      handleSelectionChange(selection, resolvedIndex)
                    }
                    onOptionUpdate={(updatedOption) =>
                      onOptionUpdate(supplier.splitId, updatedOption)
                    }
                    onOptionAdd={(newOption) =>
                      onOptionAdd(supplier.splitId, newOption)
                    }
                    onOptionDelete={(optionId) =>
                      onOptionDelete(supplier.splitId, optionId)
                    }
                    isExpired={isExpired}
                    quickEtasMins={quickEtasMins}
                    instantDeliveryMinimumDeliveryCost={
                      instantDeliveryMinimumDeliveryCost
                    }
                    orderData={orderData}
                    garaazSupplierIdFromConfig={garaazSupplierIdFromConfig}
                    isSingleGaraazInvoiceRequired={isSingleInvoiceEnabled}
                    onSplitConfigApplied={(update) =>
                      onSupplierSplitConfigUpdate(supplier.splitId, update)
                    }
                    canRemoveParts={canRemovePartsFromSplits}
                    onRemovePart={(partIndex) =>
                      onRemovePartFromSplit?.(supplier.splitId, partIndex)
                    }
                    onSelectedVehicleTypeChange={(vehicleType) => {
                      void handleSelectedVehicleTypeChange(
                        supplier.splitId,
                        vehicleType,
                      );
                    }}
                  />
                );
              })}
            </VStack>
          </Box>
        </ModalBody>

        {/* Footer */}
        <ModalFooter
          bg="white"
          borderTop="1px solid"
          borderColor="gray.200"
          py={3}
          px={6}
        >
          <VStack w="100%" spacing={3} align="stretch">
            <HStack
              w="100%"
              bg="red.50"
              border="1px solid"
              borderColor="red.100"
              borderRadius="md"
              px={3}
              py={2}
              spacing={2}
              justify="center"
            >
              <Icon as={MdWarning} color="#B91C1C" boxSize={4} />
              <Text
                fontSize="0.75rem"
                color="#B91C1C"
                fontFamily="'Nunito','Inter', sans-serif"
                fontWeight="800"
                letterSpacing="wider"
                textAlign="center"
              >
                PLEASE REVIEW CAREFULLY! AFTER SHARING OR PROCEEDING, CHANGES
                CANNOT BE UNDONE.
              </Text>
            </HStack>

            <Flex w="100%" justify="space-between" align="center">
              {/* Left side - Expiry note and Share Estimate button */}
              <HStack spacing={3} align="center">
                {onShareEstimate && (
                  <Tooltip
                    label={shareTooltipLabel}
                    isDisabled={!isShareDisabled}
                    hasArrow
                    placement="top"
                    shouldWrapChildren
                  >
                    <Button
                      variant="outline"
                      onClick={handleShareEstimateClick}
                      isLoading={isSharingEstimate}
                      isDisabled={isShareDisabled}
                      fontFamily="'Nunito','Inter', sans-serif"
                      fontWeight="600"
                      size="sm"
                      px={3}
                      borderRadius="full"
                      borderColor="blue.400"
                      borderWidth="1.5px"
                      color="blue.500"
                      bg="white"
                      _hover={{
                        bg: "blue.50",
                        borderColor: "blue.500",
                        color: "blue.600",
                      }}
                      _disabled={{
                        bg: "gray.300",
                        borderColor: "gray.400",
                        color: "gray.500",
                        cursor: "not-allowed",
                        opacity: 1,
                      }}
                      transition="all 0.2s ease"
                    >
                      PUSH ESTIMATE TO CUSTOMER
                    </Button>
                  </Tooltip>
                )}
                <Text
                  fontSize="0.75rem"
                  color="gray.500"
                  fontFamily="'Nunito','Inter', sans-serif"
                  maxW="40%"
                >
                  {isExpired
                    ? "Options expired. Modal will close automatically."
                    : ""}
                </Text>
              </HStack>

              {/* Right side - Buttons: Hold Order | Cancel Order | Proceed */}
              <HStack spacing={3}>
                {/* Hold Order Button - Subtle gray/yellow outline */}
                {onHoldOrder && (
                  <Tooltip
                    label={digitalEstimateProceedTooltip}
                    isDisabled={!isActionBlockedByDigitalEstimate}
                    hasArrow
                    placement="top"
                    shouldWrapChildren
                  >
                    <Button
                      variant="outline"
                      onClick={handleHoldOrder}
                      isDisabled={isActionBlockedByDigitalEstimate}
                      fontFamily="'Nunito','Inter', sans-serif"
                      fontWeight="600"
                      size="sm"
                      px={3}
                      borderRadius="full"
                      borderColor="yellow.500"
                      color="yellow.500"
                      bg="white"
                      cursor={
                        isActionBlockedByDigitalEstimate
                          ? "not-allowed"
                          : "pointer"
                      }
                      _hover={{
                        bg: "yellow.50",
                        borderColor: "yellow.400",
                        color: "yellow.700",
                      }}
                      transition="all 0.2s ease"
                    >
                      HOLD ESTIMATE
                    </Button>
                  </Tooltip>
                )}
                {/* Cancel Order Button - Red outline */}
                {onCancelOrder && (
                  <Tooltip
                    label={digitalEstimateProceedTooltip}
                    isDisabled={!isActionBlockedByDigitalEstimate}
                    hasArrow
                    placement="top"
                    shouldWrapChildren
                  >
                    <Button
                      variant="outline"
                      onClick={onCancellationModalOpen}
                      isDisabled={isActionBlockedByDigitalEstimate}
                      fontFamily="'Nunito','Inter', sans-serif"
                      fontWeight="600"
                      size="sm"
                      px={3}
                      borderRadius="full"
                      borderColor="red.400"
                      borderWidth="1.5px"
                      color="red.500"
                      bg="white"
                      cursor={
                        isActionBlockedByDigitalEstimate
                          ? "not-allowed"
                          : "pointer"
                      }
                      _hover={{
                        bg: "red.50",
                        borderColor: "red.500",
                        color: "red.600",
                      }}
                      transition="all 0.2s ease"
                    >
                      CANCEL ESTIMATE
                    </Button>
                  </Tooltip>
                )}
                {/* Proceed Button - Blue filled */}
                <Tooltip
                  label={proceedBlockedTooltip}
                  isDisabled={!proceedBlockedTooltip}
                  hasArrow
                  placement="top"
                  shouldWrapChildren
                >
                  <Button
                    bg="#4299E1"
                    color="white"
                    isDisabled={!canConfirm}
                    onClick={handleConfirm}
                    fontFamily="'Nunito','Inter', sans-serif"
                    fontWeight="600"
                    size="sm"
                    px={3}
                    borderRadius="full"
                    _hover={{
                      bg: "#3182CE",
                      transform: "translateY(-1px)",
                      shadow: "md",
                    }}
                    _active={{
                      bg: "#2B6CB0",
                      transform: "translateY(0)",
                    }}
                    _disabled={{
                      bg: "gray.300",
                      color: "gray.500",
                      cursor: "not-allowed",
                      _hover: {
                        bg: "gray.300",
                        transform: "none",
                        shadow: "none",
                      },
                    }}
                    transition="all 0.2s ease"
                  >
                    PROCEED
                  </Button>
                </Tooltip>
              </HStack>
            </Flex>
          </VStack>
        </ModalFooter>
      </ModalContent>

      {/* Order Cancellation Modal */}
      {onCancelOrder && (
        <OrderCancellationModal
          isOpen={isCancellationModalOpen}
          onClose={onCancellationModalClose}
          orderCacellationReasons={orderCancellationReasons}
          handleSubmit={handleCancelOrderSubmit}
        />
      )}
    </Modal>
  );
};

export default DeliveryOptionsModal;
